import { useState } from "react";
import { getErrorDetails } from "../../../app/appUtils";
import AppButton from "../../../components/ui/AppButton";
import { Spinner } from "../../../components/ui/common";
import { useToast } from "../../../hooks/useToast";
import {
  formatCurrency,
  formatCurrencyForTable,
  formatDate,
} from "../../../lib/utils";
import { Dispatch, Expense } from "../@types";
import {
  useCancelDispatchMutation,
  useUpdateDispatchMutation,
} from "../dispatchApiSlice";
import UpdateCrewMember from "./UpdateCrewMember";
import UpdateExpense from "./UpdateExpense";
import UpdateIncome from "./UpdateIncome";

interface ActiveDispatchViewProps {
  dispatch: Dispatch;
  onUpdate: () => void;
}

const ActiveDispatchView = ({
  dispatch,
  onUpdate,
}: ActiveDispatchViewProps) => {
  const toast = useToast();

  const [updateDispatch, { isLoading: isUpdating }] =
    useUpdateDispatchMutation();
  const [cancelDispatch, { isLoading: isCancelling }] =
    useCancelDispatchMutation();

  const [totalIncomeInput, setTotalIncomeInput] = useState("");
  const [showIncomeInput, setShowIncomeInput] = useState(false);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [newExpenseDescription, setNewExpenseDescription] = useState("");
  const [newExpenseAmount, setNewExpenseAmount] = useState("");

  const handleAddExpense = () => {
    if (!newExpenseDescription || !newExpenseAmount) {
      toast.error({
        title: "Error",
        message: "Please fill in both description and amount",
      });
      return;
    }

    const expense: Expense = {
      id: Date.now().toString(),
      description: newExpenseDescription,
      amount: parseFloat(newExpenseAmount),
    };

    setExpenses([...expenses, expense]);
    setNewExpenseDescription("");
    setNewExpenseAmount("");
  };

  const handleAddTotalIncome = () => {
    if (!totalIncomeInput || parseFloat(totalIncomeInput) <= 0) {
      toast.error({
        title: "Error",
        message: "Please enter a valid income amount",
      });
      return;
    }
    setShowIncomeInput(false);
  };

  const handleSubmitDispatch = async () => {
    try {
      const totalIncome = totalIncomeInput
        ? parseFloat(totalIncomeInput)
        : dispatch.totalIncome;

      await updateDispatch({
        id: dispatch.id,
        isCompleted: true,
        vehicleId: dispatch.vehicleId,
        crews: dispatch.crews,
        incomes: dispatch.incomes,
        expenses: expenses.length > 0 ? expenses : [],
        fuelExpense: dispatch.fuelExpense,
        fromLocation: dispatch.fromLocation,
        toLocation: dispatch.toLocation,
        approximateDistance: dispatch.approximateDistance,
        totalExpectedIncome: dispatch.totalExpectedIncome,
        totalIncomeEarned: dispatch.totalIncomeEarned,
        totalPassengers: dispatch.totalPassengers,
        perPassengerRate: dispatch.perPassengerRate,
      }).unwrap();

      toast.success({
        title: "Success",
        message: "Dispatch completed successfully",
      });

      onUpdate();
      setExpenses([]);
      setTotalIncomeInput("");
      setShowIncomeInput(false);
    } catch (err) {
      const errorMessage =
        getErrorDetails(err).message ||
        "Failed to submit dispatch. Please try again.";

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    }
  };

  const handleCancelDispatch = async () => {
    const cancelReason = prompt("Please enter a reason for cancellation:");
    if (!cancelReason) return;

    try {
      await cancelDispatch({
        dispatchId: dispatch.id,
        cancelReason: cancelReason,
      }).unwrap();

      toast.success({
        title: "Success",
        message: "Dispatch cancelled successfully",
      });

      onUpdate();
    } catch (err) {
      const errorMessage =
        getErrorDetails(err).message ||
        "Failed to cancel dispatch. Please try again.";

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    }
  };

  const totalEarned =
    dispatch.incomes?.reduce((sum, inc) => sum + inc.earnedAmount, 0) || 0;
  const totalExpected =
    dispatch.incomes?.reduce((sum, inc) => sum + inc.expectedAmount, 0) || 0;

  return (
    <div className="bg-white h-full rounded-[10px] py-4">
      <div className="w-full mx-auto">
        <div className="">
          {/* Header */}
          <div className="px-4 pb-4">Dispatch Overview</div>

          {/* Main Table */}
          <div className="overflow-x-auto px-4 sm:px-8 py-2">
            <table className="max-md:block border border-gray-200 w-full overflow-y-hidden rounded-t-[20px]">
              <thead className="max-md:hidden">
                <tr className="bg-gray-100 border-b-2 border-gray-200">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                    Trip Details
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-r border-gray-200 uppercase tracking-wider">
                    Crew Members
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-r border-gray-200 uppercase tracking-wider">
                    Trip Expenses
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-r border-gray-200 uppercase tracking-wider">
                    Trip Income
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-r border-gray-200 uppercase tracking-wider">
                    Income Earned
                  </th>
                </tr>
              </thead>
              <tbody className="max-md:block">
                <tr className="max-md:block hover:bg-gray-50 transition-colors">
                  {/* Trip Details Column */}
                  <td data-label="Trip Details" className="max-md:block max-md:border-b max-md:border-gray-200 max-md:before:content-[attr(data-label)] max-md:before:block max-md:before:mb-3 max-md:before:text-xs max-md:before:font-semibold max-md:before:uppercase max-md:before:text-primary px-6 py-6 align-top border-r border-gray-200">
                    <div className="space-y-3">
                      <div>
                        <p className="text-[13px] text-pColor font-medium">
                          Start location
                        </p>
                        <p className="text-sm font-semibold text-hColor mt-1">
                          {dispatch.fromLocation}
                        </p>
                      </div>
                      <div>
                        <p className="text-[13px] text-pColor font-medium">
                          Stop location
                        </p>
                        <p className="text-sm font-semibold text-hColor mt-1">
                          {dispatch.toLocation}
                        </p>
                      </div>

                      <div>
                        <p className="text-[13px] text-pColor font-medium">
                          Expected dist. covered
                        </p>
                        <p className="text-sm font-semibold text-hColor mt-1">
                          {dispatch.approximateDistance} km
                        </p>
                      </div>
                      <div>
                        <p className="text-[13px] text-pColor font-medium">
                          START TIME
                        </p>
                        <p className="text-sm font-semibold text-hColor mt-1">
                          {formatDate(dispatch.tripStartTime)}
                        </p>
                      </div>
                      <div>
                        <p className="text-[13px] text-pColor font-medium">
                          TOTAL PASSENGERS
                        </p>
                        <p className="text-sm font-semibold text-hColor mt-1">
                          {formatCurrencyForTable(dispatch.totalPassengers)}
                        </p>
                      </div>
                      <div>
                        <p className="text-[13px] text-pColor font-medium">
                          RATE PER PASSENGERS
                        </p>
                        <p className="text-sm font-semibold text-hColor mt-1">
                          {formatCurrency(dispatch.perPassengerRate)}
                        </p>
                      </div>
                    </div>
                  </td>
                  {/* Crew Members Column */}
                  <td data-label="Crew Members" className="max-md:block max-md:border-b max-md:border-gray-200 max-md:before:content-[attr(data-label)] max-md:before:block max-md:before:mb-3 max-md:before:text-xs max-md:before:font-semibold max-md:before:uppercase max-md:before:text-primary px-6 py-6 align-top border-r border-gray-200">
                    <div className="space-y-3">
                      <div className="">
                        <p className="text-sm text-pColor">Driver</p>
                        <p className="text-sm font-semibold text-hColor mt-1">
                          {dispatch.driverName}
                        </p>
                      </div>
                      {dispatch.crews && dispatch.crews.length > 0 ? (
                        dispatch.crews.map((crew) => (
                          <div key={crew.id} className="">
                            <p className="text-xs text-gray-500 mb-1">
                              {crew.crewType}
                            </p>
                            <p className="text-sm font-semibold text-gray-900">
                              {crew.name}
                            </p>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-400 italic">
                          No crew members assigned
                        </p>
                      )}
                    </div>

                    <UpdateCrewMember dispatch={dispatch} refresh={onUpdate} />
                  </td>

                  {/* Trip Expenses Column */}
                  <td data-label="Trip Expenses" className="max-md:block max-md:border-b max-md:border-gray-200 max-md:before:content-[attr(data-label)] max-md:before:block max-md:before:mb-3 max-md:before:text-xs max-md:before:font-semibold max-md:before:uppercase max-md:before:text-primary px-6 py-6 align-top border-r border-gray-200">
                    <div className="space-y-3">
                      {dispatch.expenses && dispatch.expenses.length > 0 ? (
                        <>
                          {dispatch.expenses.map((expense) => (
                            <div
                              key={expense.id}
                              className="flex justify-between items-start bg-red-50 rounded-lg p-3 border border-red-100"
                            >
                              <p className="text-sm text-gray-700 font-medium">
                                {expense.description}
                              </p>
                              <p className="text-sm font-semibold text-red-600">
                                {formatCurrency(expense.amount)}
                              </p>
                            </div>
                          ))}
                          <div className="pt-2 mt-2 border-t-2 border-gray-300">
                            <div className="flex justify-between items-center">
                              <p className="text-sm font-bold text-gray-700">
                                TOTAL
                              </p>
                              <p className="text-base font-bold text-red-600">
                                {formatCurrency(dispatch.totalExpense)}
                              </p>
                            </div>
                          </div>
                        </>
                      ) : (
                        <p className="text-sm text-gray-400 italic">
                          No expenses recorded
                        </p>
                      )}
                    </div>

                    <UpdateExpense dispatch={dispatch} refresh={onUpdate} />
                  </td>

                  {/* Trip Income Column */}
                  <td data-label="Trip Income" className="max-md:block max-md:border-b max-md:border-gray-200 max-md:before:content-[attr(data-label)] max-md:before:block max-md:before:mb-3 max-md:before:text-xs max-md:before:font-semibold max-md:before:uppercase max-md:before:text-primary px-6 py-6 align-top border-r border-gray-200">
                    <div className="space-y-3">
                      {dispatch.incomes && dispatch.incomes.length > 0 ? (
                        <>
                          {dispatch.incomes.map((income) => (
                            <div
                              key={income.id}
                              className="bg-blue-50 rounded-lg p-3 border border-blue-100"
                            >
                              <p className="text-sm text-gray-700 font-medium mb-2">
                                {income.description}
                              </p>
                              <div className="space-y-1">
                                <div className="flex justify-between text-xs">
                                  <span className="text-gray-500">
                                    Expected:
                                  </span>
                                  <span className="font-semibold text-gray-700">
                                    {formatCurrency(income.expectedAmount)}
                                  </span>
                                </div>
                                <div className="flex justify-between text-xs">
                                  <span className="text-gray-500">Earned:</span>
                                  <span className="font-semibold text-green-600">
                                    {formatCurrency(income.earnedAmount)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                          <div className="pt-2 mt-2 border-t-2 border-gray-300 space-y-2">
                            <div className="space-y-1">
                              <div className="flex justify-between items-center">
                                <p className="text-xs font-semibold text-gray-600">
                                  EXPECTED TOTAL
                                </p>
                                <p className="text-sm font-bold text-gray-700">
                                  {formatCurrency(totalExpected)}
                                </p>
                              </div>
                            </div>
                            <div className="space-y-1">
                              <div className="flex justify-between items-center">
                                <p className="text-xs font-semibold text-gray-600">
                                  EARNED TOTAL
                                </p>
                                <p className="text-sm font-bold text-gray-700">
                                  {formatCurrency(totalEarned)}
                                </p>
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <p className="text-sm text-gray-400 italic">
                          No income recorded
                        </p>
                      )}
                    </div>

                    <UpdateIncome dispatch={dispatch} refresh={onUpdate} />
                  </td>

                  {/* Income Earned Column */}
                  <td data-label="Income Earned" className="max-md:block max-md:border-b max-md:border-gray-200 max-md:before:content-[attr(data-label)] max-md:before:block max-md:before:mb-3 max-md:before:text-xs max-md:before:font-semibold max-md:before:uppercase max-md:before:text-primary px-6 py-6 align-top">
                    <div className="space-y-4">
                      <div className="bg-green-50 rounded-lg p-4 border-2 border-green-200">
                        <p className="text-xs text-gray-600 font-medium mb-1">
                          TOTAL EARNED
                        </p>
                        <p className="text-2xl font-bold text-green-600">
                          {formatCurrency(totalEarned)}
                        </p>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <p className="text-xs text-gray-600 font-medium mb-1">
                          NET BANKED
                        </p>
                        <p className="text-xl font-bold text-gray-900">
                          {formatCurrency(dispatch.netBanked)}
                        </p>
                      </div>

                      {/* <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Variance:</span>
                          <span
                            className={`font-semibold ${
                              totalEarned - totalExpected >= 0
                                ? "text-green-600"
                                : "text-red-600"
                            }`}
                          >
                            {formatCurrency(totalEarned - totalExpected)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Margin:</span>
                          <span className="font-semibold text-gray-900">
                            {(
                              ((totalEarned - dispatch.totalExpense) /
                                totalEarned) *
                              100
                            ).toFixed(1)}
                            %
                          </span>
                        </div>
                      </div> */}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Action Buttons */}
            <div className="flex justify-start items-center space-x-4 pt-4">
              <AppButton
                type="button"
                onClick={handleCancelDispatch}
                disabled={isCancelling}
                className="px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors text-[15px] disabled:opacity-50"
              >
                {isCancelling ? <Spinner /> : "Cancel Dispatch"}
              </AppButton>

              <AppButton
                type="button"
                onClick={handleSubmitDispatch}
                disabled={isUpdating}
                className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors text-[15px] disabled:opacity-50"
              >
                {isUpdating ? <Spinner /> : "Submit Dispatch"}
              </AppButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActiveDispatchView;
